import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Search, Users2, Sparkles } from 'lucide-react';
import SEO from '@/components/SEO';
import SocialStrip from '@/components/SocialStrip';
import ReportButton from '@/components/ReportButton';
import Pagination from '@/components/Pagination';
import { useLanguage } from '@/context/LanguageContext';

const CATEGORIES = [
  { key: 'all', tkey: 'shop.all', fallback: 'All' },
  { key: 'phone_case', tkey: 'shop.phoneCases', fallback: 'Phone Cases' },
  { key: 'sticker', tkey: 'shop.stickers', fallback: 'Stickers' },
  { key: 'label', tkey: 'shop.labels', fallback: 'Labels' },
];

const initials = (name = '') => (name || '?').split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();

const Community = () => {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [socialMap, setSocialMap] = useState({});
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  const load = (p = 1) => {
    setLoading(true);
    const params = { page: p, page_size: 24 };
    if (category !== 'all') params.category = category;
    if (search) params.search = search;
    api.get('/community-designs', { params })
      .then(({ data }) => {
        setItems(data.items || []);
        setPages(data.pages || 1);
        setTotal(data.total || 0);
        setPage(data.page || p);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [category]);
  useEffect(() => { const t = setTimeout(() => load(1), 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const ids = useMemo(() => items.map((i) => i.id), [items]);
  useEffect(() => {
    if (ids.length === 0) { setSocialMap({}); return; }
    api.post('/social/batch', { items: ids.map((id) => ({ entity_type: 'user_design', id })) })
      .then(({ data }) => {
        const m = {};
        for (const s of data) m[s.entity_id] = s;
        setSocialMap(m);
      })
      .catch(() => {});
  }, [ids]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <SEO
        title="Community — User-Created Print Designs on Jwalek"
        description="Explore custom-print designs created and shared by the Jwalek community. Like, save, rate, and remix designs from other creators."
        path="/community"
      />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl grid place-items-center jw-gradient-bg text-white">
            <Users2 className="w-5 h-5" />
          </div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold">{t('community.title', 'Community Designs')}</h1>
            <p style={{ color: 'var(--jw-text-muted)' }}>{t('community.subtitle', 'Designs made by real creators, reviewed and published by our team.')}</p>
          </div>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--jw-text-subtle)' }} />
          <Input data-testid="community-search-input" placeholder={t('community.search', 'Search community')} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-64" />
        </div>
      </div>

      <div className="mt-6 -mx-4 sm:mx-0 overflow-x-auto jw-scrollbar">
        <div className="px-4 sm:px-0 min-w-max">
          <Tabs value={category} onValueChange={setCategory}>
            <TabsList>
              {CATEGORIES.map((c) => (<TabsTrigger key={c.key} value={c.key} data-testid={`community-tab-${c.key}`}>{t(c.tkey, c.fallback)}</TabsTrigger>))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      {loading ? (
        <div className="jw-card p-16 text-center mt-8" data-testid="community-loading">
          <Sparkles className="w-8 h-8 mx-auto animate-pulse" style={{ color: 'var(--jw-text-subtle)' }} />
          <p className="mt-3" style={{ color: 'var(--jw-text-muted)' }}>{t('community.loading', 'Loading community designs…')}</p>
        </div>
      ) : items.length === 0 ? (
        <div className="jw-card p-16 text-center mt-8" data-testid="community-empty">
          <Users2 className="w-8 h-8 mx-auto" style={{ color: 'var(--jw-text-subtle)' }} />
          <p className="mt-3" style={{ color: 'var(--jw-text-muted)' }}>
            {t('community.empty', 'No community listings yet — be the first! Create a design and hit Publish from My Designs.')}
          </p>
          <Link to="/my-designs"><Button data-testid="community-cta-mydesigns" className="mt-4 jw-btn-glossy">{t('community.goToMyDesigns', 'Go to My Designs')}</Button></Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-8">
          {items.map((it) => (
            <Card key={it.id} className="jw-card overflow-hidden flex flex-col group relative" data-testid={`community-card-${it.id}`}>
              <div className="absolute top-2 right-2 z-10">
                <ReportButton entityType="user_design" entityId={it.id} />
              </div>
              <Link to={`/community/${it.id}`} className="block">
                <div className="aspect-square overflow-hidden p-2" style={{ background: 'var(--jw-bg-elev)' }}>
                  <img src={assetUrl(it.preview_url) || '/favicon.svg'} onError={(e) => { e.currentTarget.src = '/favicon.svg'; }} alt={it.title} className="w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-500" />
                </div>
              </Link>
              <div className="p-3 flex flex-col gap-2 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold text-sm truncate">{it.title}</h3>
                  {it.category && <Badge className="text-[10px]" variant="secondary">{t(`designCategory.${it.category}`, it.category.replace('_', ' '))}</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  <Avatar className="w-6 h-6">
                    <AvatarImage src={it.owner?.picture} alt={it.owner?.name} />
                    <AvatarFallback className="text-[10px]">{initials(it.owner?.name)}</AvatarFallback>
                  </Avatar>
                  <span className="text-xs truncate" style={{ color: 'var(--jw-text-muted)' }}>{it.owner?.name || t('community.anonymous', 'Anonymous')}</span>
                </div>
                {it.description && (
                  <p className="text-[11px] line-clamp-2" style={{ color: 'var(--jw-text-subtle)' }}>{it.description}</p>
                )}
                <SocialStrip entityType="user_design" entityId={it.id} initial={socialMap[it.id]}
                  onChange={(next) => setSocialMap((m) => ({ ...m, [it.id]: next }))} />
              </div>
            </Card>
          ))}
        </div>
      )}
      <Pagination
        page={page}
        pages={pages}
        total={total}
        onChange={(p) => { load(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        testIdPrefix="community-pagination"
      />
    </div>
  );
};

export const CommunityDetail = () => {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    api.get(`/community-designs/${id}`)
      .then(({ data }) => setItem(data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-4 py-16 text-center" style={{ color: 'var(--jw-text-muted)' }}>
      <p>{t('community.notAvailable', "This listing isn't available.")}</p>
      <Button onClick={() => navigate('/community')} className="mt-4 jw-btn-glossy">{t('community.back', 'Back to community')}</Button>
    </div>
  );
  if (!item) return <div className="p-10" style={{ color: 'var(--jw-text-muted)' }}>{t('common.loading', 'Loading…')}</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <SEO title={`${item.title} — Community Design`} description={item.description || 'Community design on Jwalek'} path={`/community/${id}`} />
      <div className="grid md:grid-cols-2 gap-8">
        <Card className="jw-card overflow-hidden">
          <div className="aspect-square" style={{ background: 'var(--jw-bg-elev)' }}>
            <img src={assetUrl(item.preview_url)} alt={item.title} className="w-full h-full object-contain" />
          </div>
        </Card>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            {item.category && <Badge variant="secondary">{t(`designCategory.${item.category}`, item.category.replace('_', ' '))}</Badge>}
            <Badge className="bg-emerald-500/15 text-emerald-200 border border-emerald-400/25">{t('community.approved', 'Approved')}</Badge>
          </div>
          <h1 className="font-display text-3xl font-bold mt-3">{item.title}</h1>
          <div className="flex items-center gap-2 mt-3">
            <Avatar className="w-8 h-8">
              <AvatarImage src={item.owner?.picture} alt={item.owner?.name} />
              <AvatarFallback>{initials(item.owner?.name)}</AvatarFallback>
            </Avatar>
            <span className="text-sm" style={{ color: 'var(--jw-text-muted)' }}>{t('community.by', 'By')} {item.owner?.name || t('community.anonymous', 'Anonymous')}</span>
          </div>
          {item.description && <p className="mt-4" style={{ color: 'var(--jw-text-muted)' }}>{item.description}</p>}
          {item.tags?.length > 0 && (
            <div className="mt-4 flex gap-1 flex-wrap">
              {item.tags.map((tag) => (<Badge key={tag} variant="secondary" className="text-[10px]">#{tag}</Badge>))}
            </div>
          )}
          <div className="mt-6">
            <SocialStrip entityType="user_design" entityId={item.id} layout="full" size="md" />
          </div>
          <div className="mt-4">
            <ReportButton entityType="user_design" entityId={item.id} variant="full" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Community;
